import React, {FC, useState, useEffect} from 'react'
import PlannerConfigModal from './PlannerConfigModal'
import {plannerApiService, PlannerStatusResponse} from '../services/plannerApi'

const PlannerIntegrationCard: FC = () => {
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [hasIntegration, setHasIntegration] = useState(false)
  const [linksCount, setLinksCount] = useState(0)
  const [lastSyncMessage, setLastSyncMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{type: 'success' | 'error'; text: string} | null>(null)

  const loadStatus = async () => {
    setIsLoading(true)
    try {
      const status: PlannerStatusResponse = await plannerApiService.getStatus()
      setHasIntegration(status.has_integration)
      setLinksCount(Array.isArray(status.links) ? status.links.length : 0)
    } catch {
      setHasIntegration(false)
      setLinksCount(0)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadStatus()
  }, [])

  const handleConfigSuccess = () => {
    setMessage({type: 'success', text: 'Integração Planner configurada com sucesso!'})
    loadStatus()
  }

  return (
    <>
      <div className='card h-100 w-100 d-flex flex-column'>
        <div className='card-header border-0 pt-5'>
          <div className='d-flex align-items-center'>
            <div className='symbol symbol-50px me-5'>
              <div className='symbol-label bg-light-info'>
                <i className='fas fa-tasks fs-2x text-info'></i>
              </div>
            </div>
            <div className='d-flex flex-column'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-4 mb-2'>Microsoft Planner</span>
                <span className='text-muted mt-1 fw-semibold fs-7'>
                  Sincronize tarefas entre o TaskRush e o Planner
                </span>
              </h3>
            </div>
          </div>
        </div>

        <div className='card-body pt-0 d-flex flex-column flex-grow-1'>
          <div className='mb-6 flex-grow-1'>
            <p className='text-muted fs-7 mb-4'>
              Configure o plano do Microsoft Planner da sua instância. Novas tarefas podem ser criadas também no Planner e as alterações são sincronizadas periodicamente.
            </p>

            {message && (
              <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-4`}>
                <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`} />
                {message.text}
                <button type='button' className='btn-close ms-auto' onClick={() => setMessage(null)} />
              </div>
            )}

            <div className='d-flex flex-column gap-3'>
              <div className='d-flex align-items-center justify-content-between'>
                <span className='text-muted fs-7'>Status:</span>
                <span className={`badge ${hasIntegration ? 'badge-light-success' : 'badge-light-warning'}`}>
                  {isLoading ? '...' : hasIntegration ? 'Configurado' : 'Não configurado'}
                </span>
              </div>
              {hasIntegration && linksCount === 0 && (
                <div className='text-muted fs-7'>
                  Integração iniciada – conclua vinculando um plano a um projeto em &quot;Gerenciar integração&quot;.
                </div>
              )}
              {hasIntegration && linksCount > 0 && (
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fs-7'>Planos vinculados:</span>
                  <span className='text-muted fs-7'>
                    {linksCount} {linksCount === 1 ? 'plano' : 'planos'}
                  </span>
                </div>
              )}
              {lastSyncMessage && (
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fs-7'>Última sync:</span>
                  <span className='text-muted fs-7'>{lastSyncMessage}</span>
                </div>
              )}
            </div>
          </div>

          <div className='mt-auto'>
            <div className='d-flex flex-column gap-2'>
              <button
                className='btn btn-sm btn-primary w-100'
                onClick={() => setShowConfigModal(true)}
                disabled={isLoading}
              >
                {hasIntegration ? (
                  <>
                    <i className='fas fa-cog me-2' />
                    Gerenciar integração
                  </>
                ) : (
                  <>
                    <i className='fas fa-plug me-2' />
                    Configurar integração
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <PlannerConfigModal
        show={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onSuccess={handleConfigSuccess}
      />
    </>
  )
}

export default PlannerIntegrationCard
